import React from "react";
import { connect } from "react-redux";
import { withStyles } from "@material-ui/core";
import { I18n } from "@aws-amplify/core";
import { map, get, uniqBy, forEach, sum, filter, flatten, size } from "lodash";
import clsx from "clsx";
import Box from "@material-ui/core/Box";

import { useReactToPrint } from "react-to-print";
import { fetchPost } from "../../../api/fetch";
import { setBreadcrumb } from "../../../actions/common";
import CompanyCountsForm from "./CompanyCountsForm";
import ProfilesBox from "./ProfilesBox";
import CompanyCountsDetails from "./CompanyCountsDetails";
import PrintIcon from "../../Common/PrintIcon";
import { formatDate } from "../../../utils/date";

const styles = {
  container: {
    margin: "24px",
    minHeight: "unset",
    height: "calc(100vh - 196px)",
    overflow: "auto",
    padding: "24px",
  },
  content: {
    marginTop: "24px",
    maxWidth: "815px",
  },
  gridLine1: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(373px, 1fr))",
    gridAutoRows: "234px",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
  gridLine2: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(638px, 1fr))",
    gridAutoRows: "460px",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
  gridLine3: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoRows: "max-content",
    gridGap: "24px",
    padding: "0px 24px 24px",
  },
};

const CompanyCounts = (props) => {
  const { classes, dispatch, language } = props;
  const [data, setData] = React.useState();
  const [filters, setFilters] = React.useState();
  const [expandedSites, setExpandedSites] = React.useState([]);
  const printRef = React.useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  React.useEffect(() => {
    dispatch(
      setBreadcrumb([
        {
          name: "dashboards",
          display: false,
        },
        {
          name: I18n.get("sidebar_companyCounts"),
        },
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async (filters) => {
    setFilters(filters);
    try {
      setData();

      let body = {
        filters: {
          active: true,
          company_ids: [get(filters, ["company"])],
          site_ids: get(filters, ["sites"]),
          profile_type_ids: get(filters, ["profileTypes"]),
          assigned_company_ids: [get(filters, ["assigned_company"])],
          assigned_site_ids: get(filters, ["assigned_sites"]),
        },
      };

      if (get(filters, ["company"]) === -1 || get(filters, ["company"]) === undefined) {
        delete body.filters.company_ids;
        delete body.filters.site_ids;
      }

      setFilters(body.filters);
      let res = await fetchPost("onsite/totals", body);

      // Create tree hierarchy with the result
      let data = {
        sites: uniqBy(
          map(get(res, ["counts"]), (zone) => ({
            company_id: get(zone, ["company_id"]),
            company_name: get(zone, ["company_name"]),
            site_id: get(zone, ["site_id"]),
            site_name: get(zone, ["site_name"]),
            zones: [],
          })),
          "site_id"
        ),
      };

      forEach(get(res, ["counts"]), (zone) => {
        forEach(get(res, ["profile_types"]), (profile_type) => {
          if (
            size(
              filter(
                get(zone, ["profile_types"]),
                (t) => t.profile_type_id === profile_type.id
              )
            ) === 0
          ) {
            zone.profile_types.push({
              profile_type_id: profile_type.id,
              profile_count: null,
            });
          }
        });
      });

      forEach(get(data, ["sites"]), (site) => {
        // Add zones
        site.zones = filter(
          get(res, ["counts"]),
          (zone) => zone.site_id === site.site_id
        );

        // Create total counts for each site
        site.profile_types = [];
        forEach(get(res, ["profile_types"]), (profile_type) => {
          site.profile_types.push({
            profile_type_id: profile_type.id,
            profile_count: sum(
              map(
                filter(
                  flatten(map(site.zones, (z) => z.profile_types)),
                  (t) => t.profile_type_id === profile_type.id
                ),
                (c) => c.profile_count
              )
            ),
          });
        });
      });

      // Create global total counts
      data.profile_types = [];
      forEach(get(res, ["profile_types"]), profile_type => {
        data.profile_types.push({
          id: profile_type.id,
          profile_type_name: get(profile_type, [
            `name_${language}`
          ]),
          profile_count: sum(
            map(
              filter(
                flatten(map(data.sites, s => s.profile_types)),
                t => t.profile_type_id === profile_type.id
              ),
              c => c.profile_count
            )
          ),
          offsite_profile_count: (profile_type_id => {
            const p = get(
              res,
              ["offsite_counts", "profile_types"],
              []
            ).find(p => p.profile_type_id === profile_type_id);
            return p?.profile_count;
          })(profile_type.id)
        });
      });

      setData(data);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <Box className={`mainContent ${classes.container}`}>
      <CompanyCountsForm search={search} language={language} />
      <Box className={clsx(classes.content, "print-margin")} ref={printRef}>
        {data && (
          <Box style={{ textAlign: "right" }}>
            <PrintIcon handlePrint={handlePrint} />
          </Box>
        )}
        <Box className="only-print">{formatDate(Date.now())}</Box>
        <ProfilesBox
          title={I18n.get("dashboard_totalOffsiteCounts")}
          data={
            data
              ? {
                ...data,
                profile_types: data.profile_types.map(p => ({
                  ...p,
                  profile_count: p.offsite_profile_count
                }))
              }
              : undefined
          }
          filters={{ ...filters, site_ids: [-1], zone_ids: [-1] }}
        />
        <ProfilesBox
          title={I18n.get("dashboard_totalOnsiteCounts")}
          data={data}
          filters={filters}
        />
        <CompanyCountsDetails
          data={data}
          refresh={() => {
            search({
              company: get(filters, ["company_ids", 0]),
              sites: get(filters, ["site_ids"]),
              profileTypes: get(filters, ["profile_type_ids"]),
              assigned_company: get(filters, ["assigned_company_ids"])[0],
              assigned_sites: get(filters, ["assigned_site_ids"])
            });
          }}
          filters={
            filters
              ? {
                assigned_company_ids: get(filters, ["assigned_company_ids"]),
                assigned_site_ids: get(filters, ["assigned_site_ids"])
              }
              : undefined
          }
          expandedSites={expandedSites}
          setExpandedSites={setExpandedSites}
        />
        <Box className="only-print">{window.location.href}</Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = (state) => {
  return {
    language: state.user.language,
  };
};

export default connect(mapStateToProps)(withStyles(styles)(CompanyCounts));
